import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import { GET_CONTACT_FILTER_FIELDS } from '../../../lib/graphql/queries/contacts';
import { useFlywheelEnabled } from '../../../stores/useOrganizationStore';
import {
  FLYWHEEL_ONLY_FIELDS,
  type ContactFilterField,
} from '../../../types/contactFilter.types';

interface GetContactFilterFieldsData {
  contactFilterFields: ContactFilterField[];
}

export interface ContactFilterFieldGroup {
  label: string;
  fields: ContactFilterField[];
}

/**
 * The catalogue of fields the advanced contact filter can reference.
 *
 * Fields, their permitted operators, and the value sets for enum fields all come
 * from the resolver, so the builder can only ever offer a filter the server can
 * actually run. A field added to the backend catalogue appears here with no
 * frontend change.
 */
export const useContactFilterFields = (organizationId: string | undefined, enabled = true) => {
  const { data, loading, error } = useQuery<GetContactFilterFieldsData>(GET_CONTACT_FILTER_FIELDS, {
    variables: { organizationId: organizationId ?? '' },
    skip: !enabled || !organizationId,
    fetchPolicy: 'cache-first',
  });

  // The catalogue is server-built and only gates `inFlywheel`, so an org
  // without SMS is still offered SMS Consent as a custom condition. The
  // catalogue is also cached, so it can still list `inFlywheel` after the org
  // loses Flywheel. Drop both here: the builder already discards a card whose
  // field has no catalogue entry, so an existing condition disappears with it.
  const flywheelEnabled = useFlywheelEnabled();
  const fields = useMemo<ContactFilterField[]>(() => {
    const all = data?.contactFilterFields ?? [];
    return flywheelEnabled ? all : all.filter((f) => !FLYWHEEL_ONLY_FIELDS.has(f.name));
  }, [data, flywheelEnabled]);

  const fieldsByName = useMemo(() => new Map(fields.map((field) => [field.name, field])), [fields]);

  // Grouped for the field dropdown. Catalogue order is meaningful (the backend
  // lists fields the way the UI should read them), so groups appear in the
  // order their first field does rather than alphabetically.
  const groups = useMemo<ContactFilterFieldGroup[]>(() => {
    const byLabel = new Map<string, ContactFilterField[]>();
    for (const field of fields) {
      const existing = byLabel.get(field.group);
      if (existing) existing.push(field);
      else byLabel.set(field.group, [field]);
    }
    return Array.from(byLabel, ([label, groupFields]) => ({ label, fields: groupFields }));
  }, [fields]);

  return { fields, fieldsByName, groups, isLoading: loading, error };
};

export default useContactFilterFields;
